import type { Invoice, InvoiceItem } from '@clinic-os/shared/types';
import { DEMO_INVOICES, DEMO_CLINIC_ID } from '../data/demo-data';

export interface InvoiceService {
  getAll: (clinicId?: string) => Promise<Invoice[]>;
  getById: (id: string) => Promise<Invoice | null>;
  getByPatient: (patientId: string) => Promise<Invoice[]>;
  create: (data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Invoice>;
  update: (id: string, data: Partial<Invoice>) => Promise<Invoice>;
  markPaid: (id: string) => Promise<Invoice>;
  markSent: (id: string) => Promise<Invoice>;
  getOutstanding: (clinicId?: string) => Promise<{ count: number; total: number }>;
}

class InvoiceServiceImpl implements InvoiceService {
  private invoices: Invoice[] = [...DEMO_INVOICES];

  async getAll(clinicId = DEMO_CLINIC_ID): Promise<Invoice[]> {
    return this.invoices
      .filter((i) => i.clinicId === clinicId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async getById(id: string): Promise<Invoice | null> {
    return this.invoices.find((i) => i.id === id) ?? null;
  }

  async getByPatient(patientId: string): Promise<Invoice[]> {
    return this.invoices.filter((i) => i.patientId === patientId);
  }

  async create(data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>): Promise<Invoice> {
    const now = new Date().toISOString();
    const invoice: Invoice = {
      ...data,
      id: `inv${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    this.invoices.push(invoice);
    return invoice;
  }

  async update(id: string, data: Partial<Invoice>): Promise<Invoice> {
    const idx = this.invoices.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error(`Invoice ${id} not found`);
    const updated: Invoice = { ...this.invoices[idx]!, ...data, updatedAt: new Date().toISOString() };
    this.invoices[idx] = updated;
    return updated;
  }

  async markPaid(id: string): Promise<Invoice> {
    return this.update(id, { status: 'paid', paidAt: new Date().toISOString() });
  }

  async markSent(id: string): Promise<Invoice> {
    return this.update(id, { status: 'sent' });
  }

  async getOutstanding(clinicId = DEMO_CLINIC_ID): Promise<{ count: number; total: number }> {
    const outstanding = this.invoices.filter(
      (i) => i.clinicId === clinicId && (i.status === 'sent' || i.status === 'overdue')
    );
    return {
      count: outstanding.length,
      total: outstanding.reduce((sum, i) => sum + i.total, 0),
    };
  }
}

export const invoiceService = new InvoiceServiceImpl();
